import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { employeeAPI } from '../services/api';

const BulkUploadEmployeeDialog = ({ isOpen, onClose, onSave }) => {
  const [file, setFile] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.type !== 'text/csv' && !selectedFile.name.endsWith('.csv')) {
        setMessage({ type: 'error', text: 'Please select a CSV file' });
        setFile(null);
        setPreview(null);
        return;
      }
      setFile(selectedFile);
      setMessage({ type: '', text: '' });
      
      // Preview CSV content
      const reader = new FileReader();
      reader.onload = (event) => {
        const text = event.target.result;
        const lines = text.split('\n').filter(line => line.trim());
        if (lines.length > 0) {
          setPreview({
            header: lines[0],
            sampleRows: lines.slice(1, 6),
            totalRows: lines.length - 1
          });
        }
      };
      reader.readAsText(selectedFile);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      setMessage({ type: 'error', text: 'Please select a CSV file' });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await employeeAPI.bulkUpload(formData);
      
      setMessage({ 
        type: 'success', 
        text: `Successfully uploaded ${response.data.created || 0} employees. ${response.data.skipped || 0} skipped (duplicates).` 
      });
      
      setTimeout(() => {
        onSave();
        handleClose();
      }, 2000);
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to upload employees. Please check the CSV format.'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setPreview(null);
    setMessage({ type: '', text: '' });
    onClose();
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div 
      className="bulk-upload-dialog-overlay"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 10001,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflowY: 'auto',
        padding: '20px'
      }}
      onClick={handleClose}
    >
      <div 
        className="bulk-upload-dialog-content"
        style={{
          backgroundColor: 'white',
          padding: '25px',
          borderRadius: '12px',
          minWidth: '600px',
          maxWidth: '800px',
          width: '100%',
          boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
          maxHeight: '90vh',
          overflowY: 'auto'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '20px',
          borderBottom: '1px solid #e5e7eb',
          paddingBottom: '15px'
        }}>
          <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 600, color: '#1f2937' }}>
            Bulk Upload Employees
          </h2>
          <button 
            onClick={handleClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#6b7280',
              padding: '0',
              width: '30px',
              height: '30px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '4px',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#f3f4f6';
              e.target.style.color = '#1f2937';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = '#6b7280';
            }}
          >
            ×
          </button>
        </div>

        <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f9fafb', borderRadius: '8px', fontSize: '0.875rem', color: '#6b7280' }}>
          <strong>CSV Format:</strong> The CSV file should have the following columns:
          <ul style={{ margin: '10px 0 0 20px', padding: 0 }}>
            <li><strong>name</strong> - Employee full name (required)</li>
            <li><strong>email</strong> - Email address (required, must be unique)</li>
            <li><strong>department</strong> - Department name (optional)</li>
            <li><strong>deliveryUnit</strong> - Delivery Unit: DU1, DU2, DU3, DU4, DU5, or Other (optional, defaults to DU1)</li>
          </ul>
          <p style={{ margin: '10px 0 0 0' }}>
            <strong>Example:</strong><br />
            name,email,department,deliveryUnit<br />
            John Doe,john.doe@example.com,Engineering,DU1<br />
            Jane Smith,jane.smith@example.com,Marketing,DU2
          </p>
        </div>

        {message.text && (
          <div className={`alert alert-${message.type}`} style={{ marginBottom: '20px' }}>
            {message.text}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="csv-file">Select CSV File *</label>
            <input
              type="file"
              id="csv-file"
              accept=".csv"
              onChange={handleFileChange}
              required
              style={{
                padding: '8px',
                border: '1px solid rgba(0, 0, 0, 0.2)',
                borderRadius: 'var(--radius-md)',
                fontSize: '0.875rem',
                width: '100%'
              }}
            />
          </div>

          {preview && (
            <div style={{ 
              marginBottom: '20px', 
              padding: '15px', 
              backgroundColor: '#f9fafb', 
              borderRadius: '8px',
              border: '1px solid #e5e7eb'
            }}>
              <strong style={{ display: 'block', marginBottom: '10px' }}>
                Preview ({preview.totalRows} rows found):
              </strong>
              <div style={{ 
                fontSize: '0.75rem', 
                fontFamily: 'monospace',
                backgroundColor: 'white',
                padding: '10px',
                borderRadius: '4px',
                overflowX: 'auto',
                maxHeight: '200px',
                overflowY: 'auto'
              }}>
                <div style={{ fontWeight: 'bold', marginBottom: '5px', color: '#7c3aed' }}>
                  {preview.header}
                </div>
                {preview.sampleRows.map((row, idx) => (
                  <div key={idx} style={{ marginBottom: '3px' }}>
                    {row}
                  </div>
                ))}
                {preview.totalRows > 5 && (
                  <div style={{ color: '#6b7280', fontStyle: 'italic', marginTop: '5px' }}>
                    ... and {preview.totalRows - 5} more rows
                  </div>
                )}
              </div>
            </div>
          )}

          <div style={{ 
            display: 'flex', 
            gap: '10px', 
            justifyContent: 'flex-end',
            marginTop: '20px',
            paddingTop: '20px',
            borderTop: '1px solid #e5e7eb'
          }}>
            <button 
              type="button" 
              className="btn btn-secondary" 
              onClick={handleClose}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn btn-primary" 
              disabled={loading || !file}
            >
              {loading ? 'Uploading...' : 'Upload Employees'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default BulkUploadEmployeeDialog;
